import { html } from 'lit-element';

import { LightLitElement } from '@elab/components';
import sanitizeHTML from '@elab/core/utils/sanitizeHTML';
import connect from '@elab/core/store/connect';
import { actions as coreActions } from '@elab/core/store/core';
import { prepareHtml } from '@elab/core/utils/webComponent';
import analytics from '@elab/core/utils/analytics';

import { actions, reducers, sagaFactory, selectors, storeName } from './redux';

export class PrivacyPreferencesForm extends LightLitElement {
    static get properties() {
        return {
            togglerLabelYes: { type: String, attribute: 'toggler-label-yes' },
            togglerLabelNo: { type: String, attribute: 'toggler-label-no' },
            togglerAlignment: { type: String, attribute: 'toggler-alignment' },
            iconClass: { type: String, attribute: 'icon-class' },
            hideOtherGroup: { type: Boolean, attribute: 'hide-other-group' },
            takeTooltipAside: {
                type: Boolean,
                attribute: 'take-tooltip-aside',
            },
            fullSizeTooltip: { type: Boolean, attribute: 'full-size-tooltip' },
            toggleTooltipState: {
                type: Boolean,
                attribute: 'toggle-tooltip-state',
            },
            submitOnChange: { type: Boolean, attribute: 'submit-on-change' },
            submitDelay: { type: Number, attribute: 'submit-delay' },
            ignoreSuccessMessage: {
                type: Boolean,
                attribute: 'ignore-success-message',
            },
            preferenceGroups: { type: Array, attribute: false },
            preferences: { type: Object, attribute: false },
            otherGroup: { type: Object, attribute: false },
            analyticsConfig: { type: String, attribute: 'analytics-config' },
        };
    }

    constructor() {
        super();
        this.togglerLabelYes = '';
        this.togglerLabelNo = '';
        this.togglerAlignment = 'left';
        this.iconClass = '';
        this.preferenceGroups = [];
        this.preferences = [];
        this.otherGroup = {};
        this.analyticsConfig = '';
    }

    afterConnect() {
        this.dispatchEvent(new CustomEvent('component-connect'));
        analytics.handlePrivacySettingsLoading();
    }

    beforeDisconnect() {
        this.dispatchEvent(new CustomEvent('component-disconnect'));
    }

    submit() {
        this.dispatchEvent(
            new CustomEvent('submit-preferences', {
                detail: {
                    submitDelay: this.submitDelay,
                    ignoreSuccessMessage: this.ignoreSuccessMessage,
                },
            }),
        );
    }

    handleInput(id) {
        const { value, switchOn, switchOff } = this.preferences[id] || {};
        const newValue = !value;
        const switchArr = newValue ? switchOn : switchOff;

        this.dispatchEvent(
            new CustomEvent('set-present-preference', {
                detail: { label: id, action: newValue },
            }),
        );

        const newPreferences = Object.entries(this.preferences).reduce((acc, [key, pref]) => {
            return {
                ...acc,
                [key]: {
                    ...pref,
                    value: id === key || switchArr.includes(key) ? newValue : pref.value,
                },
            };
        }, {});

        this.dispatchEvent(
            new CustomEvent('set-preferences', {
                detail: newPreferences,
            }),
        );

        if (this.submitOnChange) {
            this.submit();
        }
    }

    handleRadioChange(id, newValue) {
        const newPreferences = { ...this.preferences };
        newPreferences[id].radioValue = newValue;

        this.dispatchEvent(
            new CustomEvent('set-preferences', {
                detail: newPreferences,
            }),
        );
    }

    render() {
        return html`
            <v-slot name="top-button"></v-slot>
            ${!this.hideOtherGroup ? this.groupTemplate(this.otherGroup) : html``}
            ${this.preferenceGroups.map(this.groupTemplate)}
            <v-slot name="privacy-disclaimer"></v-slot>
            <v-slot name="bottom-button"></v-slot>
        `;
    }

    createLabel(name, index) {
        return `${index}. ${name}`;
    }

    groupTemplate = ({ name = '', description = '', items = [] }, index) => {
        return !items.length
            ? html``
            : html`
                  <div class="privacy-preferences__group">
                      ${name &&
                          html`
                              <h4 class="privacy-preferences__group-parent-name">
                                  ${this.createLabel(name, index + 1)}
                              </h4>
                          `}
                      ${description &&
                          html`
                              <h4 class="privacy-preferences__group-description">
                                  ${description}
                              </h4>
                          `}
                      ${items.map(this.togglerTemplate)}
                  </div>
              `;
    };

    togglerTemplate = ({ id, name = '', description = '', values, tooltip }) => {
        const template = html`
            <e2-toggler
                align="${this.togglerAlignment}"
                name="${id}"
                without-label
                @change="${() => this.handleInput(id)}"
                caption-yes="${this.togglerLabelYes}"
                caption-no="${this.togglerLabelNo}"
                .checked="${this.preferences[id].value}"
                css-class="toggler__wrapper--privacy-preferences"
                class="privacy-preferences__item"
            >
                <div v-slot="toggler__label" class="privacy-preferences__group-label">
                    <h3 class="privacy-preferences__consent-name">
                        ${name} ${this.takeTooltipAside ? '' : this.tooltipTemplate(tooltip)}
                    </h3>
                    ${this.takeTooltipAside ? this.tooltipTemplate(tooltip) : ''}
                    ${description &&
                        `<p class="privacy-preferences__consent-description">
                            ${description}
                        </p>`}
                </div>
                ${values
                    ? html`
                          <div v-slot="toggler__content">
                              ${this.radioButtonTemplate({ id, values })}
                          </div>
                      `
                    : ''}
            </e2-toggler>
        `;

        return html`
            <e2-click-tracking analytics-config="${this.analyticsConfig}" type="alerts-${id}">
                ${template}
            </e2-click-tracking>
        `;
    };

    tooltipTemplate(content) {
        return !content
            ? html``
            : html`
                  <e2-tooltip
                      show-on-hover
                      ?is-full-size="${this.fullSizeTooltip}"
                      ?toggle-state="${this.toggleTooltipState}"
                  >
                      <i
                          tabindex="1"
                          class="icon tooltip-icon ${this.iconClass || 'icon-info'}"
                          v-slot="target"
                      ></i>
                      <div v-slot="content">
                          ${html([sanitizeHTML(prepareHtml(content))])}
                      </div>
                  </e2-tooltip>
              `;
    }

    radioButtonTemplate({ id, values }) {
        const { radioValue } = this.preferences[id] || {};
        return html`
            <e2-radio-group
                name="${id}"
                @change_radio="${({ detail }) => this.handleRadioChange(id, detail)}"
                .value="${radioValue}"
                .values="${values}"
                class="privacy-preferences__item privacy-preferences__item--radio"
            ></e2-radio-group>
        `;
    }
}

const mapStateToProps = state => ({
    preferenceGroups: selectors.getPreferenceGroups(state),
    preferences: selectors.getPreferences(state),
    otherGroup: selectors.getOtherGroup(state),
});

const mapDispatchToEvents = dispatch => ({
    'component-connect': () => {
        dispatch(coreActions.addReducer(storeName, reducers));
        const saga = sagaFactory();
        dispatch(coreActions.addSaga(storeName, saga));
        dispatch(actions.setInitialized());
    },
    'component-disconnect': () => {
        dispatch(coreActions.removeSaga(storeName));
        dispatch(coreActions.removeReducer(storeName));
    },
    'set-preferences': ({ detail }) => {
        dispatch(actions.setPreferences(detail));
    },
    'submit-preferences': ({ detail }) => {
        dispatch(actions.doSubmit(detail));
    },
    'set-present-preference': ({ detail }) => {
        dispatch(actions.submitCurrentPreference(detail));
    },
});

export default () =>
    customElements.define(
        'e2-privacy-preferences-form',
        connect(mapStateToProps, mapDispatchToEvents)(PrivacyPreferencesForm),
    );
